import { equal, notEqual } from '@/api/helpers';
import { handleAxiosError } from '@/api/helpers/api-errors.helper';
import { client } from '@/client';
import { Course, MaterialType, MigrateCourseMode } from '@/client/courses';
import { LoadingStatuses } from '@/common/constants';
import { CoursesSelectInput } from '@/components/form/selectors';
import { useToast } from '@/hooks/useToast';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { Dialog } from 'primereact/dialog';
import { RadioButton, RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type CourseMigrationModalProps = {
  visible: boolean;
  onHide: () => void;
  courseId: string;
  onSuccess?: () => void;
};

export const CourseMigrationModal: React.FC<CourseMigrationModalProps> = ({
  visible,
  onHide,
  courseId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [migrationOldCourse, setMigrationOldCourse] = useState<Course | null>(
    null,
  );
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationMode, setMigrationMode] = useState<MigrateCourseMode>(
    MigrateCourseMode.EVERYTHING,
  );

  const additionalFilters = useMemo(
    () => [
      equal('account', null),
      notEqual('id', courseId),
      equal('type', MaterialType.COURSE),
    ],
    [courseId],
  );

  const handleMigrateCourse = async () => {
    if (!migrationOldCourse?.id || !courseId) {
      return;
    }

    if (migrationOldCourse.id === courseId) {
      toast?.error(t('toast.error'), t('course.migration.error.sameIds'));
      return;
    }

    setIsMigrating(true);
    try {
      await client.courses.migrateCourse({
        oldCourseId: migrationOldCourse.id,
        newCourseId: courseId,
        mode: migrationMode,
      });

      toast?.success(t('toast.success'), t('course.migration.success'));

      setMigrationOldCourse(null);
      onSuccess?.();
      onHide();
    } catch (e) {
      handleAxiosError(e as any, toast);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleClose = () => {
    if (!isMigrating) {
      setMigrationOldCourse(null);
      setMigrationMode(MigrateCourseMode.EVERYTHING);
      onHide();
    }
  };

  const footer = () => {
    return (
      <FlexContainer justify="flex-end" gap={12}>
        <AppButton
          label={t('button.cancel')}
          severity="secondary"
          type="outlined"
          onClick={handleClose}
          isDisabled={isMigrating}
        />
        <AppButton
          label={
            isMigrating
              ? t('course.migration.migrating')
              : t('course.migration.migrate')
          }
          severity="warning"
          onClick={handleMigrateCourse}
          isDisabled={isMigrating || !migrationOldCourse?.id}
          state={isMigrating ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
        />
      </FlexContainer>
    );
  };

  return (
    <StyledDialog
      visible={visible}
      onHide={handleClose}
      header={t('course.migration.title')}
      footer={footer()}
    >
      <FlexContainer direction="column" gap={'var(--medium-padding)'}>
        <p className="w-full m-0">{t('course.migration.description')}</p>
        <div className="field w-full">
          <label htmlFor="migration-old-course-id">
            {t('course.migration.oldCourse')}
          </label>
          <CoursesSelectInput
            id="migration-old-course-id"
            className="w-full"
            selectedValue={migrationOldCourse}
            onChange={setMigrationOldCourse}
            additionalFilters={additionalFilters}
            placeholder={t('course.migration.oldCourse.placeholder')}
            isDisabled={isMigrating}
          />
        </div>
        <div className="field w-full">
          <label>{t('course.migration.mode')}</label>
          <FlexContainer
            direction="row"
            gap={'var(--default-padding)'}
            align="center"
            wrap="wrap"
            justify="flex-start"
          >
            <div className="field-radio" data-id="migration-mode-everything">
              <label htmlFor="migration-mode-everything">
                <RadioButton
                  className="p-circle"
                  inputId="migration-mode-everything"
                  name="migrationMode"
                  value={MigrateCourseMode.EVERYTHING}
                  checked={migrationMode === MigrateCourseMode.EVERYTHING}
                  onChange={(e: RadioButtonChangeEvent) =>
                    setMigrationMode(e.value as MigrateCourseMode)
                  }
                  disabled={isMigrating}
                />
                <span>{t('course.migration.mode.everything')}</span>
              </label>
            </div>
            <div className="field-radio" data-id="migration-mode-feedback-only">
              <label htmlFor="migration-mode-feedback-only">
                <RadioButton
                  className="p-circle"
                  inputId="migration-mode-feedback-only"
                  name="migrationMode"
                  value={MigrateCourseMode.FEEDBACK_ONLY}
                  checked={migrationMode === MigrateCourseMode.FEEDBACK_ONLY}
                  onChange={(e: RadioButtonChangeEvent) =>
                    setMigrationMode(e.value as MigrateCourseMode)
                  }
                  disabled={isMigrating}
                />
                <span>{t('course.migration.mode.feedbackOnly')}</span>
              </label>
            </div>
          </FlexContainer>
        </div>
      </FlexContainer>
    </StyledDialog>
  );
};
